import { useContext, useRef } from "react";
import { useRouter } from "next/router";
import useUser from "@src/features/app/hooks/useUser";
import {
  resolveFeatureFlag,
  useFeatureFlagsLoaded,
} from "@src/features/app/hooks/useFeatureFlag";
import { FeatureFlagContext } from "@src/lib/contexts/FeatureFlagContext";
import {
  audienceFor,
  getActiveRelease,
  ReleaseAudience,
  ReleaseExperience,
  ReleaseHeadlineRelease,
} from "./releases";

// `?release_preview=<slug>` shows a release outside its window, ignores
// dismissal, and replays the celebration on every load so copy can be QA'd.
// Add `&style=card|envelope|nav-letter` to preview a specific experience.
const PREVIEW_QUERY_PARAM = "release_preview";
const STYLE_QUERY_PARAM = "style";
const EXPERIENCES: ReleaseExperience[] = ["card", "envelope", "nav-letter"];

export type VisibleRelease = {
  release: ReleaseHeadlineRelease;
  audience: ReleaseAudience;
  experience: ReleaseExperience;
  isPreview: boolean;
};

// The release this viewer should see right now, if any: in its window (or
// previewed), for their role, and past its feature flag. The homepage and the
// left nav both read it, so they always agree on which release is live.
const useVisibleRelease = (): VisibleRelease | null => {
  const { user } = useUser();
  const { query, isReady } = useRouter();
  const previewParam = query[PREVIEW_QUERY_PARAM];
  const previewSlug =
    typeof previewParam === "string" ? previewParam : undefined;
  const release = getActiveRelease(new Date(), previewSlug);
  const flags = useContext(FeatureFlagContext);
  const flagOn = release?.flag ? resolveFeatureFlag(flags, release.flag) : true;
  const flagsLoaded = useFeatureFlagsLoaded();
  // Flags can refetch (e.g. when the org changes), briefly reading as unloaded
  // and off; keep the first answer so the release doesn't vanish mid-open.
  const flagChoice = useRef<boolean | null>(null);
  if (flagChoice.current === null && flagsLoaded) {
    flagChoice.current = flagOn;
  }

  // Statically optimized pages have an empty query until the router is
  // ready; waiting avoids mounting the wrong (non-preview) variant.
  if (
    !isReady ||
    !user ||
    !release ||
    !audienceFor(release).includes(user.userType as ReleaseAudience)
  ) {
    return null;
  }
  const isPreview = Boolean(previewSlug);
  // A flagged release waits for flags, then shows only where the flag is on.
  if (release.flag && !isPreview && !flagChoice.current) {
    return null;
  }

  const styleParam = query[STYLE_QUERY_PARAM];
  const previewExperience = EXPERIENCES.find(
    (experience) => previewSlug && experience === styleParam,
  );
  return {
    release,
    audience: user.userType as ReleaseAudience,
    experience: previewExperience ?? release.experience,
    isPreview,
  };
};

export default useVisibleRelease;
